/**
 * Wishlist Plus — Public addToWishlist API (merchant theme script)
 */
(function () {
    'use strict';

    if (window.__WlPublicApiLoaded) return;
    window.__WlPublicApiLoaded = true;

    var FALLBACK_STRINGS = {
        'extension.toast.added': 'Added to wishlist',
        'extension.toast.added_to_lists': 'Added to {count} list(s)'
    };

    function t(path, vars) {
        var wp = window.wishlistPlus || {};
        var str = wp.t ? wp.t(path, vars) : undefined;
        if (str === undefined || str === path) {
            str = FALLBACK_STRINGS[path] || path;
            if (vars && typeof str === 'string') {
                Object.keys(vars).forEach(function (key) {
                    str = str.replace(new RegExp('\\{' + key + '\\}', 'g'), String(vars[key]));
                });
            }
        }
        return str;
    }

    function buildResult(overrides) {
        return Object.assign({
            success: false,
            status: 'error',
            action: null,
            productId: null,
            variantId: null,
            wishlistIds: [],
            addedCount: null,
            guestUuid: null,
            message: '',
            error: null
        }, overrides || {});
    }

    function normalizeWishlistIds(raw) {
        if (!raw) return [];
        if (!Array.isArray(raw)) raw = [raw];
        return raw.map(function (id) { return parseInt(id, 10); }).filter(function (id) {
            return !isNaN(id) && id > 0;
        });
    }

    function dispatchWlEvent(name, detail) {
        try {
            document.dispatchEvent(new CustomEvent(name, {
                bubbles: true,
                detail: detail
            }));
        } catch (e) { /* ignore */ }
    }

    /**
     * Refresh header wishlist badge.
     * @param {Object} h - window.wlHelpers
     * @param {Object} [opts]
     * @param {boolean} [opts.delayed] — also refresh after a short delay (multi-list modal
     *   resolves before the background save finishes, so an immediate count can be stale)
     */
    async function refreshHeaderCount(h, opts) {
        opts = opts || {};
        if (!h || typeof h.refreshHeaderWishlistCount !== 'function') return;

        try {
            await h.refreshHeaderWishlistCount();
        } catch (e) {
            console.error('[wishlistPlus.addToWishlist] Header count refresh failed:', e);
        }

        if (opts.delayed) {
            setTimeout(function () {
                h.refreshHeaderWishlistCount().catch(function () { /* ignore */ });
            }, 400);
        }
    }

    function helpersReady() {
        return !!(window.wlHelpers && typeof window.apiHandlerWishlist === 'function');
    }

    function waitForHelpers(attempt) {
        attempt = attempt || 0;
        return new Promise(function (resolve) {
            if (helpersReady()) {
                resolve(window.wlHelpers);
                return;
            }
            if (attempt > 50) {
                resolve(null);
                return;
            }
            setTimeout(function () {
                waitForHelpers(attempt + 1).then(resolve);
            }, 100);
        });
    }

    async function postAddWishlist(h, productId, variantId, productHandle, wishlistIds) {
        var payload = Object.assign({
            product_id: productId,
            variant_id: variantId,
            product_handle: productHandle
        }, h.getIdentityPayload());

        if (wishlistIds && wishlistIds.length) {
            payload.wishlist_ids = wishlistIds;
        }

        return window.apiHandlerWishlist('/add-wishlist', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * @param {Object} params
     * @param {number} params.productId
     * @param {number} params.variantId
     * @param {string} params.productHandle
     * @param {number[]} [params.wishlistIds]
     * @param {boolean} [params.showModal=true]
     * @param {boolean} [params.showToast=true]
     * @param {boolean} [params.showLogin=true]
     * @returns {Promise<Object>}
     */
    async function addToWishlist(params) {
        params = params || {};

        var productId = parseInt(params.productId != null ? params.productId : params.product_id, 10);
        var variantId = parseInt(params.variantId != null ? params.variantId : params.variant_id, 10);
        var productHandle = String(
            params.productHandle != null ? params.productHandle : (params.product_handle || '')
        ).trim();
        var wishlistIds = normalizeWishlistIds(params.wishlistIds || params.wishlist_ids);
        var showModal = params.showModal !== false;
        var showToastFlag = params.showToast !== false;
        var showLogin = params.showLogin !== false;

        var base = {
            productId: isNaN(productId) ? null : productId,
            variantId: isNaN(variantId) ? null : variantId
        };

        if (!productId || !variantId || !productHandle) {
            return buildResult(Object.assign({}, base, {
                status: 'validation_error',
                message: 'productId, variantId, and productHandle are required',
                error: 'validation_error'
            }));
        }

        var h = await waitForHelpers(0);
        if (!h) {
            return buildResult(Object.assign({}, base, {
                status: 'error',
                message: 'Wishlist Plus is not ready. Enable the Wishlist Plus app embed.',
                error: 'not_ready'
            }));
        }

        if (h.isGuest && !h.guestWishlistEnabled) {
            if (showLogin && h.showLoginPopup) h.showLoginPopup();
            return buildResult(Object.assign({}, base, {
                status: 'login_required',
                message: 'Login required to add to wishlist',
                error: 'login_required'
            }));
        }

        // Multi-wishlist: explicit list ids → silent add (no modal)
        if (h.multipleWishlistsEnabled && wishlistIds.length > 0) {
            try {
                var multiData = await postAddWishlist(h, productId, variantId, productHandle, wishlistIds);
                if (!(multiData && multiData.success)) {
                    return buildResult(Object.assign({}, base, {
                        status: 'error',
                        message: (multiData && multiData.message) || 'Failed to add item to wishlist',
                        error: (multiData && (multiData.error || multiData.message)) || 'add_failed',
                        wishlistIds: wishlistIds
                    }));
                }

                if (h.handleGuestResponse) h.handleGuestResponse(multiData);
                if (showToastFlag && h.showToast) {
                    h.showToast(
                        t('extension.toast.added_to_lists', {
                            count: multiData.added_count || wishlistIds.length
                        }),
                        '/apps/wishlist'
                    );
                }
                await refreshHeaderCount(h);

                var multiResult = buildResult(Object.assign({}, base, {
                    success: true,
                    status: 'added',
                    action: 'added',
                    wishlistIds: (multiData.wishlist_ids || wishlistIds).map(Number),
                    addedCount: multiData.added_count != null ? multiData.added_count : wishlistIds.length,
                    guestUuid: multiData.guest_uuid || null,
                    message: multiData.message || 'Item added to wishlist successfully'
                }));
                dispatchWlEvent('wishlistPlus:added', multiResult);
                return multiResult;
            } catch (multiErr) {
                console.error('[wishlistPlus.addToWishlist] Error:', multiErr);
                return buildResult(Object.assign({}, base, {
                    status: 'error',
                    message: 'Failed to add item to wishlist',
                    error: (multiErr && multiErr.message) || 'add_failed',
                    wishlistIds: wishlistIds
                }));
            }
        }

        // Multi-wishlist: list picker modal
        if (h.multipleWishlistsEnabled && showModal) {
            if (!h.openWishlistListModal || !h.fetchCustomerLists) {
                return buildResult(Object.assign({}, base, {
                    status: 'error',
                    message: 'Multiple wishlists UI is not available',
                    error: 'not_ready'
                }));
            }

            try {
                var lists = await h.fetchCustomerLists();
                if (!lists || lists.length === 0) {
                    lists = await h.fetchCustomerLists(true);
                }

                var modalResult = await h.openWishlistListModal({
                    mode: 'add',
                    lists: lists || [],
                    preselectedIds: [],
                    productId: productId,
                    variantId: variantId,
                    productHandle: productHandle
                });

                var selectedIds = (modalResult && modalResult.wishlistIds)
                    ? modalResult.wishlistIds.map(Number)
                    : [];
                var selectedCount = (modalResult && modalResult.count) || selectedIds.length;

                if (showToastFlag && h.showToast) {
                    h.showToast(
                        t('extension.toast.added_to_lists', { count: selectedCount || 1 }),
                        '/apps/wishlist'
                    );
                }
                // Modal completes optimistically before the save API finishes.
                await refreshHeaderCount(h, { delayed: true });

                var modalOk = buildResult(Object.assign({}, base, {
                    success: true,
                    status: 'added',
                    action: 'added',
                    wishlistIds: selectedIds,
                    addedCount: selectedCount,
                    message: 'Item added to wishlist successfully'
                }));
                dispatchWlEvent('wishlistPlus:added', modalOk);
                return modalOk;
            } catch (modalErr) {
                if (modalErr && modalErr.message === 'cancelled') {
                    return buildResult(Object.assign({}, base, {
                        status: 'cancelled',
                        message: 'List selection cancelled',
                        error: 'cancelled'
                    }));
                }
                console.error('[wishlistPlus.addToWishlist] Modal error:', modalErr);
                return buildResult(Object.assign({}, base, {
                    status: 'error',
                    message: 'Failed to add item to wishlist',
                    error: (modalErr && modalErr.message) || 'add_failed'
                }));
            }
        }

        if (h.multipleWishlistsEnabled && !showModal) {
            return buildResult(Object.assign({}, base, {
                status: 'validation_error',
                message: 'wishlistIds are required when showModal is false and multiple wishlists are enabled',
                error: 'wishlist_ids_required'
            }));
        }

        // Single list — logged-in or guest
        try {
            var guestUUID = h.getGuestUUID ? h.getGuestUUID() : null;
            if ((h.isLoggedIn || (h.guestWishlistEnabled && guestUUID)) && h.checkProductWishlistStatus) {
                try {
                    var existing = await h.checkProductWishlistStatus(productId, variantId);
                    if (existing && existing.isWishlisted) {
                        return buildResult(Object.assign({}, base, {
                            success: true,
                            status: 'already_added',
                            action: null,
                            wishlistIds: existing.wishlistIds || [],
                            message: 'Item is already in the wishlist'
                        }));
                    }
                } catch (checkErr) {
                    // Continue to add
                }
            }

            var data = await postAddWishlist(h, productId, variantId, productHandle, null);

            if (!(data && data.success)) {
                return buildResult(Object.assign({}, base, {
                    status: 'error',
                    message: (data && data.message) || 'Failed to add item to wishlist',
                    error: (data && (data.error || data.message)) || 'add_failed'
                }));
            }

            if (h.handleGuestResponse) h.handleGuestResponse(data);
            if (showToastFlag && h.showToast) {
                h.showToast(t('extension.toast.added'), '/apps/wishlist');
            }
            await refreshHeaderCount(h);

            var ok = buildResult(Object.assign({}, base, {
                success: true,
                status: 'added',
                action: 'added',
                wishlistIds: data.wishlist_ids ? data.wishlist_ids.map(Number) : [],
                addedCount: data.added_count != null ? data.added_count : 1,
                guestUuid: data.guest_uuid || (h.getGuestUUID ? h.getGuestUUID() : null),
                message: data.message || 'Item added to wishlist successfully'
            }));
            dispatchWlEvent('wishlistPlus:added', ok);
            return ok;
        } catch (err) {
            console.error('[wishlistPlus.addToWishlist] Error:', err);
            return buildResult(Object.assign({}, base, {
                status: 'error',
                message: 'Failed to add item to wishlist',
                error: (err && err.message) || 'add_failed'
            }));
        }
    }

    window.wishlistPlus = window.wishlistPlus || {};
    window.wishlistPlus.addToWishlist = addToWishlist;
    window.wishlistPlus.ready = true;
    dispatchWlEvent('wishlistPlus:ready', { api: 'addToWishlist' });
})();

